import { useCallback, useEffect, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import SettingsIcon from "@mui/icons-material/Settings";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Typography,
} from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import api from "../../utils/api";
import CalendarSettingsModal from "./components/CalendarSettingsModal";
import CreateBatchLessonsModal from "./components/CreateBatchLessonsModal";
import DeleteBatchLessonsModal from "./components/DeleteBatchLessonsModal";
import CreateLessonModal from "./components/CreateLessonModal";
import {
    calendarRangeToApiDates,
    lessonRowToFullCalendarEvent,
    toServerDateTime,
} from "./calendarUtils";

function timeHmToFcSlot(hm) {
    if (!hm || typeof hm !== "string") return "09:00:00";
    const t = hm.trim();
    return t.length === 5 ? `${t}:00` : t;
}

function addIsoDurationToHms(hms, isoDuration) {
    if (!hms) return hms;
    const parts = hms.split(":").map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return hms;
    let totalSecs = parts[0] * 3600 + parts[1] * 60 + parts[2];
    const m = String(isoDuration || "").match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
    if (!m) return hms;
    totalSecs += Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
    if (totalSecs > 24 * 3600) totalSecs = 24 * 3600;
    const hh = Math.floor(totalSecs / 3600);
    const mm = Math.floor((totalSecs % 3600) / 60);
    const ss = totalSecs % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

export default function Calendar() {
    const { mode, systemMode } = useColorScheme();
    const isDark = (systemMode || mode) === "dark";
    const [calendarSettings, setCalendarSettings] = useState(null);
    const [settingsLoadState, setSettingsLoadState] = useState("loading");

    const [range, setRange] = useState({ start: null, end: null });
    const [events, setEvents] = useState([]);
    const [classrooms, setClassrooms] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [planningMode, setPlanningMode] = useState(false);
    const [selectedTeacherId, setSelectedTeacherId] = useState("");

    const [createOpen, setCreateOpen] = useState(false);
    const [createInitialAt, setCreateInitialAt] = useState("");
    const [createInitialEndAt, setCreateInitialEndAt] = useState("");
    const [editLesson, setEditLesson] = useState(null);
    const [busyInfoOpen, setBusyInfoOpen] = useState(false);
    const [busyInfoText, setBusyInfoText] = useState("");

    const [settingsOpen, setSettingsOpen] = useState(false);
    const [batchOpen, setBatchOpen] = useState(false);
    const [deleteBatchOpen, setDeleteBatchOpen] = useState(false);

    const classroomById = useMemo(
        () => new Map(classrooms.map((c) => [Number(c.id), c])),
        [classrooms]
    );

    const defaultClassroomId = classrooms[0]?.id != null ? String(classrooms[0].id) : "";

    const refreshEvents = useCallback(async () => {
        if (!range.start || !range.end) {
            setEvents([]);
            return;
        }
        const apiDates = calendarRangeToApiDates(range);
        if (!apiDates) {
            setEvents([]);
            return;
        }

        if (planningMode) {
            if (!selectedTeacherId) {
                setEvents([]);
                return;
            }
            try {
                const res = await api.get(`admin/teachers/${selectedTeacherId}/planning-calendar`, {
                    params: { start: apiDates.start, end: apiDates.end },
                });
                const payload = res.data?.data || {};
                const busy = (payload.busy_events || []).map((e) => ({
                    ...e,
                    editable: false,
                    startEditable: false,
                    durationEditable: false,
                }));
                const lessons = (payload.lesson_events || []).map((e) => ({
                    ...e,
                    editable: true,
                    startEditable: true,
                    durationEditable: true,
                }));
                setEvents([...busy, ...lessons]);
            } catch (e) {
                console.error(e);
                setEvents([]);
            }
            return;
        }

        try {
            const res = await api.get("admin/classroom-lessons", {
                params: { start: apiDates.start, end: apiDates.end },
            });
            const rows = res.data?.data || [];
            const next = rows.map((lesson) =>
                lessonRowToFullCalendarEvent(lesson, classroomById)
            );
            setEvents(next);
        } catch (e) {
            console.error(e);
            setEvents([]);
        }
    }, [range.start, range.end, classroomById, planningMode, selectedTeacherId]); // eslint-disable-line react-hooks/exhaustive-deps

    const persistLessonTimes = async (event) => {
        const start = event.start;
        const end = event.end;
        if (!start || !end) return;
        const p = event.extendedProps || {};
        const body = {
            starts_at: toServerDateTime(start),
            ends_at: toServerDateTime(end),
            status: p.status || "planned",
            call_type: p.call_type || "languageon",
            call_link: p.call_link || null,
            notes: p.notes || null,
        };
        if (p.classroom_id != null) {
            body.classroom_id = Number(p.classroom_id);
        }
        await api.put(`admin/classroom-lessons/${event.id}`, body);
    };

    const handleEventDrop = async (info) => {
        try {
            await persistLessonTimes(info.event);
            await refreshEvents();
        } catch (e) {
            info.revert();
        }
    };

    const handleEventResize = async (info) => {
        try {
            await persistLessonTimes(info.event);
            await refreshEvents();
        } catch (e) {
            info.revert();
        }
    };

    useEffect(() => {
        let cancel = false;
        (async () => {
            setSettingsLoadState("loading");
            try {
                const res = await api.get("admin/calendar/settings");
                if (cancel) return;
                setCalendarSettings(res.data?.data ?? null);
                setSettingsLoadState("ready");
            } catch (e) {
                console.error(e);
                if (!cancel) {
                    setCalendarSettings(null);
                    setSettingsLoadState("error");
                }
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const res = await api.get("admin/classrooms", { params: { page: 1, perPage: 500 } });
                if (cancel) return;
                setClassrooms(res.data?.data || []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    useEffect(() => {
        let cancel = false;
        (async () => {
            try {
                const res = await api.get("admin/teachers", { params: { page: 1, perPage: 500 } });
                if (cancel) return;
                setTeachers(res.data?.data || []);
            } catch (e) {
                console.error(e);
            }
        })();
        return () => {
            cancel = true;
        };
    }, []);

    useEffect(() => {
        if (settingsLoadState !== "ready" || !calendarSettings) return;
        refreshEvents();
    }, [refreshEvents, settingsLoadState, calendarSettings]);

    useEffect(() => {
        if (settingsLoadState !== "ready" || !calendarSettings) return;
        const intervalId = setInterval(() => {
            refreshEvents();
        }, 10000);
        return () => clearInterval(intervalId);
    }, [refreshEvents, settingsLoadState, calendarSettings]);

    if (settingsLoadState === "loading") {
        return (
            <Box sx={{ p: 4, display: "flex", alignItems: "center", gap: 2 }}>
                <CircularProgress size={28} />
                <Typography color="text.secondary">Loading calendar settings…</Typography>
            </Box>
        );
    }

    if (settingsLoadState === "error" || !calendarSettings) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
                    Calendar
                </Typography>
                <Typography color="error">Could not load calendar settings. Try again later.</Typography>
            </Box>
        );
    }

    const slotMinTime = timeHmToFcSlot(calendarSettings.display_start_time);
    const slotDuration = calendarSettings.slot_duration_iso || "00:30:00";
    const slotMaxTime = addIsoDurationToHms(
        timeHmToFcSlot(calendarSettings.display_end_time),
        slotDuration
    );
    const hiddenDays = Array.isArray(calendarSettings.non_working_days)
        ? calendarSettings.non_working_days.map((d) => Number(d))
        : [];

    const renderEventContent = (arg) => {
        const viewType = arg.view.type || "";
        const isMonth = viewType === "dayGridMonth";
        const isList = viewType.startsWith("list");
        const wrapMonth = (inner) => {
            if (!isMonth) return inner;
            const bg = arg.event.backgroundColor || "#0d6efd";
            const fg = arg.event.textColor || "#fff";
            const bd = arg.event.borderColor || bg;
            return (
                <Box
                    sx={{
                        bgcolor: bg,
                        color: fg,
                        border: `1px solid ${bd}`,
                        borderRadius: "4px",
                        px: 0.5,
                        py: 0.25,
                        width: "100%",
                        minHeight: 24,
                        boxSizing: "border-box",
                        overflow: "hidden",
                    }}
                >
                    {inner}
                </Box>
            );
        };

        const p = arg.event.extendedProps || {};
        const kind = p.kind;
        if (kind === "weekly_busy" || kind === "exception_busy") {
            return wrapMonth(
                <Box sx={{ overflow: "hidden", px: 0.25, py: 0.25 }}>
                    <Typography variant="caption" sx={{ lineHeight: 1.2 }} noWrap>
                        {arg.event.title}
                    </Typography>
                </Box>
            );
        }

        const lessonId = arg.event.id != null && arg.event.id !== "" ? String(arg.event.id) : "";
        const lessonIdLabel = lessonId ? `#${lessonId}` : "";

        const durationMinutes =
            Number(p.duration_minutes) ||
            (arg.event.start && arg.event.end
                ? Math.max(
                      0,
                      Math.round((arg.event.end.getTime() - arg.event.start.getTime()) / 60000)
                  )
                : 0);
        const tier = isList || isMonth
            ? "month"
            : durationMinutes > 0 && durationMinutes < 20
              ? "tiny"
              : durationMinutes > 0 && durationMinutes < 45
                ? "compact"
                : "full";

        const avatarSrc = p.teacher_avatar || null;
        const classroom = p.classroom_name || p.display_classroom || arg.event.title;
        const teacherName = p.display_teacher || p.teacher_name || "";
        const initial = (teacherName || classroom || "?").trim().charAt(0).toUpperCase() || "?";
        const tooltip = [
            lessonIdLabel,
            arg.timeText,
            classroom,
            teacherName ? `— ${teacherName}` : "",
        ]
            .filter(Boolean)
            .join(" ")
            .trim();

        if (tier === "tiny") {
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        overflow: "hidden",
                        minHeight: 0,
                        width: "100%",
                        height: "100%",
                        px: 0.25,
                        lineHeight: 1,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 10,
                            fontWeight: 600,
                            lineHeight: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            flex: 1,
                            minWidth: 0,
                        }}
                        title={tooltip}
                    >
                        {lessonIdLabel ? `${lessonIdLabel} · ` : ""}
                        {arg.timeText ? `${arg.timeText} · ` : ""}
                        {classroom}
                    </Typography>
                </Box>
            );
        }

        if (tier === "compact") {
            return (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        overflow: "hidden",
                        minHeight: 0,
                        width: "100%",
                        height: "100%",
                        px: 0.25,
                    }}
                >
                    {avatarSrc ? (
                        <Avatar
                            src={avatarSrc}
                            sx={{ width: 16, height: 16, flexShrink: 0, fontSize: 9 }}
                        />
                    ) : (
                        <Avatar sx={{ width: 16, height: 16, flexShrink: 0, fontSize: 9 }}>
                            {initial}
                        </Avatar>
                    )}
                    <Box
                        sx={{
                            minWidth: 0,
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            lineHeight: 1.05,
                        }}
                        title={tooltip}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                lineHeight: 1.05,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {lessonIdLabel ? `${lessonIdLabel} · ` : ""}
                            {arg.timeText ? `${arg.timeText} · ` : ""}
                            {classroom}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        const avatarSx = isMonth
            ? { width: 20, height: 20, flexShrink: 0, mt: 0.125, fontSize: 10 }
            : isList
              ? { width: 24, height: 24, flexShrink: 0, mt: 0.125, fontSize: 12 }
              : { width: 22, height: 22, flexShrink: 0, mt: 0.125, fontSize: 11 };
        return wrapMonth(
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0.5,
                    overflow: "hidden",
                    minHeight: 0,
                    width: "100%",
                }}
                title={tooltip}
            >
                {avatarSrc ? (
                    <Avatar src={avatarSrc} sx={avatarSx} />
                ) : (
                    <Avatar sx={avatarSx}>{initial}</Avatar>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {lessonIdLabel ? (
                        <Typography
                            variant="caption"
                            component="div"
                            color="inherit"
                            sx={{ lineHeight: 1.1, opacity: 0.95, fontWeight: 700 }}
                            noWrap
                        >
                            {isList ? `Lesson ${lessonIdLabel}` : lessonIdLabel}
                        </Typography>
                    ) : null}
                    {arg.timeText ? (
                        <Typography
                            variant="caption"
                            component="div"
                            color="inherit"
                            sx={{ lineHeight: 1.1, opacity: 0.95 }}
                            noWrap
                        >
                            {arg.timeText}
                        </Typography>
                    ) : null}
                    <Typography variant="caption" component="div" fontWeight={600} noWrap sx={{ lineHeight: 1.15 }}>
                        {classroom}
                    </Typography>
                    {teacherName ? (
                        <Typography
                            variant="caption"
                            component="div"
                            sx={{ lineHeight: 1.1, opacity: 0.9 }}
                            noWrap
                        >
                            {teacherName}
                        </Typography>
                    ) : null}
                </Box>
            </Box>
        );
    };

    const handleEventClick = (info) => {
        const p = info.event.extendedProps || {};
        const kind = p.kind;
        if (kind === "weekly_busy") {
            setBusyInfoText("Recurring unavailable time: the teacher can change this under Weekly hours.");
            setBusyInfoOpen(true);
            return;
        }
        if (kind === "exception_busy") {
            setBusyInfoText("One-time unavailable block (exception).");
            setBusyInfoOpen(true);
            return;
        }
        const ev = info.event;
        const start = ev.start;
        const end = ev.end;
        if (!start || !end || p.classroom_id == null) return;
        setEditLesson({
            id: String(ev.id),
            classroom_id: p.classroom_id,
            starts_at: start.toISOString(),
            ends_at: end.toISOString(),
            status: p.status || "planned",
            call_type: p.call_type || "languageon",
            call_link: p.call_link ?? "",
            notes: p.notes ?? "",
        });
        setCreateOpen(true);
    };

    const closeLessonModal = (done) => {
        setCreateOpen(false);
        setCreateInitialAt("");
        setCreateInitialEndAt("");
        setEditLesson(null);
        if (done) refreshEvents();
    };

    const handleDeleteLesson = async (lessonId, lessonStatus) => {
        if (!lessonId) return;
        const status = String(lessonStatus || editLesson?.status || "").toLowerCase();
        const message =
            status === "done"
                ? "This lesson is already completed. Deleting will hide it from lists and reports. Proceed?"
                : "Delete this event?";
        if (!window.confirm(message)) return;
        try {
            await api.delete(`admin/classroom-lessons/${lessonId}`);
            closeLessonModal(true);
        } catch (e) {
            alert(e?.response?.data?.message || "Failed to delete event.");
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Calendar
            </Typography>

            <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 2 }}
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                useFlexGap
            >
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={planningMode}
                                onChange={(_, checked) => setPlanningMode(checked)}
                                color="primary"
                            />
                        }
                        label="Planning mode"
                    />
                    <FormControl size="small" sx={{ minWidth: 240 }} disabled={!planningMode}>
                        <Select                          
                            value={selectedTeacherId}
                            onChange={(e) => setSelectedTeacherId(e.target.value)}
                            displayEmpty
                        >
                            <MenuItem value="">
                                <em>Select teacher…</em>
                            </MenuItem>
                            {teachers.map((t) => (
                                <MenuItem key={t.id} value={String(t.id)}>
                                    {[t.first_name, t.last_name].filter(Boolean).join(" ").trim() ||
                                        t.email ||
                                        `Teacher #${t.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {planningMode && !selectedTeacherId ? (
                        <Typography variant="body2" color="text.secondary">
                            Choose a teacher to load busy hours and their lessons.
                        </Typography>
                    ) : null}
                </Stack>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => setSettingsOpen(true)}
                    >
                        Calendar settings
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<PlaylistAddIcon />}
                        onClick={() => setBatchOpen(true)}
                    >
                        Create lessons
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<PlaylistRemoveIcon />}
                        onClick={() => setDeleteBatchOpen(true)}
                    >
                        Delete lessons
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setEditLesson(null);
                            setCreateInitialAt("");
                            setCreateInitialEndAt("");
                            setCreateOpen(true);
                        }}
                    >
                        Create lesson
                    </Button>
                </Stack>
            </Stack>

            {isDark ? (
                <style>{`
                    .lo-calendar-dark {
                        border-color: #55575b !important;
                    }
                    .lo-calendar-dark .fc {
                        --fc-border-color: #55575b;
                        --fc-page-bg-color: transparent;
                        --fc-neutral-bg-color: #2e2e2e;
                        --fc-list-day-bg-color: #2e2e2e;
                    }
                    .lo-calendar-dark .fc-theme-standard td,
                    .lo-calendar-dark .fc-theme-standard th,
                    .lo-calendar-dark .fc-scrollgrid,
                    .lo-calendar-dark .fc-scrollgrid td,
                    .lo-calendar-dark .fc-scrollgrid th,
                    .lo-calendar-dark .fc-timegrid-slot,
                    .lo-calendar-dark .fc-timegrid-axis-frame {
                        border-color: #55575b !important;
                    }
                    .lo-calendar-dark .fc-col-header,
                    .lo-calendar-dark .fc-col-header-cell,
                    .lo-calendar-dark .fc-timegrid-axis,
                    .lo-calendar-dark .fc-list-day-cushion,
                    .lo-calendar-dark .fc-scrollgrid-section-header th {
                        background-color: #2e2e2e !important;
                    }
                `}</style>
            ) : null}
            <Box
                className={isDark ? "lo-calendar-dark" : undefined}
                sx={{
                    border: `1px solid ${isDark ? "#55575b" : "#e5e7eb"}`,
                    borderRadius: 0,
                    overflow: "hidden",
                    "& .fc-timegrid-slot": {
                        height: "30px",
                    },
                    "& .fc-daygrid-event": {
                        borderRadius: "4px",
                        marginTop: "2px",
                        marginBottom: "2px",
                    },
                    "& .fc-daygrid-event .fc-event-main": {
                        padding: "1px 2px",
                    },
                    "& .fc-timegrid-event": {
                        padding: 0,
                    },
                    "& .fc-timegrid-event .fc-event-main": {
                        padding: "1px 2px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        height: "100%",
                        overflow: "hidden",
                    },
                    "& .fc-timegrid-event .fc-event-main-frame": {
                        height: "100%",
                    },
                }}
            >
                <FullCalendar
                    key={`${slotMinTime}-${slotMaxTime}-${slotDuration}-${hiddenDays.join(",")}`}
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    height="auto"
                    allDaySlot={false}
                    contentHeight="800px"
                    views={{
                        dayGridMonth: {
                            eventDisplay: "block",
                        },
                    }}
                    headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
                    }}
                    buttonText={{
                        today: "Today",
                        month: "Month",
                        week: "Week",
                        day: "Day",
                        list: "List",
                    }}
                    slotMinTime={slotMinTime}
                    slotMaxTime={slotMaxTime}
                    slotDuration={slotDuration}
                    slotLabelInterval={slotDuration}
                    eventMinHeight={18}
                    eventShortHeight={30}
                    hiddenDays={hiddenDays.length ? hiddenDays : undefined}
                    selectable
                    editable
                    eventDurationEditable
                    events={events}
                    eventContent={renderEventContent}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventResize}
                    datesSet={(info) => {
                        const start = info.startStr;
                        const end = info.endStr;
                        setRange((prev) => {
                            if (prev.start === start && prev.end === end) {
                                return prev;
                            }
                            return { start, end };
                        });
                    }}
                    slotEventOverlap={false}
                    select={(info) => {
                        setEditLesson(null);
                        setCreateInitialAt(info.startStr || "");
                        setCreateInitialEndAt(info.endStr || "");
                        setCreateOpen(true);
                    }}
                />
            </Box>

            <Dialog open={busyInfoOpen} onClose={() => setBusyInfoOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Unavailable time</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">{busyInfoText}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBusyInfoOpen(false)}>OK</Button>
                </DialogActions>
            </Dialog>

            <CalendarSettingsModal
                open={settingsOpen}
                settings={calendarSettings}
                onSaved={(data) => setCalendarSettings(data)}
                onClose={() => setSettingsOpen(false)}
            />

            <CreateBatchLessonsModal
                open={batchOpen}
                onClose={(done) => {
                    setBatchOpen(false);
                    if (done) refreshEvents();
                }}
                classrooms={classrooms}
                defaultClassroomId={defaultClassroomId}
                hiddenWeekdays={hiddenDays}
            />

            <DeleteBatchLessonsModal
                open={deleteBatchOpen}
                onClose={(done) => {
                    setDeleteBatchOpen(false);
                    if (done) refreshEvents();
                }}
                classrooms={classrooms}
                defaultClassroomId={defaultClassroomId}
            />

            <CreateLessonModal
                open={createOpen}
                onClose={closeLessonModal}
                classrooms={classrooms}
                defaultClassroomId={defaultClassroomId}
                initialStartsAt={createInitialAt}
                initialEndsAt={createInitialEndAt}
                editLesson={editLesson}
                onDelete={handleDeleteLesson}
            />
        </Box>
    );
}
